import React, { type FC } from "react";
import { BusinessList } from "@src/components/business-list";
import { ListItem } from "@src/components/business-list";
import { BusinessMeasureValue } from "@src/components/business-data-display";
import type { StockKanbanContainer, StockKanbanContainerContent } from "../../../../../../api/stockKanbanContainers";
import { formatStorePlaceCellName } from "../../../../helpers/formatStorePlaceCellName";
import type { UseStockKanbanSupplyContentsQueryResult } from "../../hooks";

/** Пропсы компонента {@link StockKanbanSupplyContentsList}. */
interface StockKanbanSupplyContentsListProps {
    /** Контейнер КАНБАН. */
    container: StockKanbanContainer;
    /** Query-result выбранных партий контейнера. */
    contentsQuery: UseStockKanbanSupplyContentsQueryResult;
    /**
     * Режим только просмотра. \
     * Скрывает создание; открытие строки остаётся для просмотра формы.
     */
    readonly: boolean;
    /** Открывает форму создания партии. */
    onCreateContent: () => void;
    /**
     * Открывает форму партии (редактирование или просмотр).
     * @param contentCode код строки выбранной партии
     */
    onOpenContent: (contentCode: number) => void;
}

/** Список партий, выбранных для комплектования контейнера КАНБАН. */
export const StockKanbanSupplyContentsList: FC<StockKanbanSupplyContentsListProps> = ({
    container,
    contentsQuery,
    readonly,
    onCreateContent,
    onOpenContent,
}) => {
    const { content, isFetching } = contentsQuery;

    return (
        <BusinessList
            isLoad={isFetching}
            hoodBtns={[
                {
                    text: "Скомлектовать",
                    srcIcon: "/images/common/plus.svg",
                    hide: readonly,
                    onClick: onCreateContent,
                },
            ]}
        >
            {content.map((item) => {
                // const isFullyCollected = item.Qty >= container.Qty;

                return (
                    <ListItem
                        key={item.Code}
                        // tone={isFullyCollected ? "success" : "default"}
                        title={`Партия №${item.Lot?.LotNum ?? item.LotCode}`}
                        props={[
                            { label: "Номер партии", value: item.Lot?.LotNum },
                            {
                                label: "Кол-во",
                                value: <BusinessMeasureValue value={item.Qty} measure={container.Measure} />,
                            },
                            { label: "Ячейка", value: formatKanbanContentStorePlace(item) },
                        ]}
                        onClick={() => {
                            onOpenContent(item.Code);
                        }}
                    />
                );
            })}
        </BusinessList>
    );
};

/** Суммирует количество выбранных партий контейнера. */
export function sumKanbanContentQty(content: UseStockKanbanSupplyContentsQueryResult["content"]): number {
    return content.reduce((sum, item) => sum + item.Qty, 0);
}

/**
 * Подпись ячейки размещения выбранной партии. \
 * `SpCode = 0` — партия без размещения («Не размещена в ячейке»).
 * @param content строка партии контейнера
 */
function formatKanbanContentStorePlace(content: Pick<StockKanbanContainerContent, "SpCode" | "StorePlace">): string {
    if (content.SpCode === 0) {
        return "<Не размещена в ячейке>";
    }

    const cellName = formatStorePlaceCellName(content.StorePlace);
    return cellName !== "" ? cellName : `Ячейка ${content.SpCode}`;
}
